import { ApplicationContextProvider } from 'application/contexts/ApplicationContext';
import { DictionaryContextProvider } from 'application/contexts/DictionaryContext';
import { HistoryProvider } from 'application/contexts/HistoryContext';
import { PageContextProvider } from 'application/contexts/PageContext';
import { SiteContextProvider } from 'application/contexts/SiteContext';
import Application from 'application/entities/Application';
import { NextPage } from 'next';
import type { AppProps as NextAppProps } from 'next/app';
import { StrictMode, useEffect, useRef } from 'react';
import 'ui/styles/globals.scss';

/**
 * The `DXPContentPage` type is the result of _app.tsx's modifications to page props.
 */
export type DXPContentPage<TProps extends { content?: Content.PageContent }> = NextPage<TProps['content'] & Omit<TProps, 'content' | 'session'>>;

const App: React.FC<NextAppProps> = ({ Component, pageProps }) => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const { content, dictionary, site, host } = (pageProps as any) ?? {};
	const { culture } = content?.page ?? {};

	/**
	 * When the App component is mounted we transfer application values from pageProps to
	 * a client-side exposed instance of the Application entity that can be
	 * accessed in adapters, repositories and other non React client-side modules
	 *
	 * If you want to access the values inside a React module you can use the
	 * ApplicationContext
	 */

	const isMounted = useRef(false);

	if (!isMounted.current) {
		// The code here, executes before render - on mount
		// if (url) Application.setUrl(url);
		if (culture) Application.setLocale(culture);
	}

	useEffect(() => {
		// Initial render has been completed, set isMounted true
		isMounted.current = true;
	}, []);

	useEffect(() => {
		// Values that change between renders must be updated continuously.
		//
		// Fx. when changing language with the language selector, the page
		// is not reloaded and the locale must be updated.
		if (culture) Application.setLocale(culture);
	});

	return (
		<StrictMode>
			<ApplicationContextProvider application={null}>
				<SiteContextProvider site={site}>
					<DictionaryContextProvider dictionary={dictionary ?? []} culture={culture}>
						<HistoryProvider>
							<PageContextProvider content={content} host={host}>
								<Component {...content} {...pageProps} />
							</PageContextProvider>
						</HistoryProvider>
					</DictionaryContextProvider>
				</SiteContextProvider>
			</ApplicationContextProvider>
		</StrictMode>
	);
};

export default App;
